import React, { useEffect, useMemo, useState } from 'react';
import {
  getAllTerminals,
  createTerminal,
  updateTerminal,
  deleteTerminal,
  assignTerminal,
  unassignTerminal,
  getClients,
  getTerminalRemoteConfig,
  updateTerminalRemoteConfig,
} from '../../services/api';
import { FaPlus, FaEdit, FaTrash, FaLink, FaUnlink, FaSearch, FaTimes, FaSpinner, FaExclamationTriangle } from 'react-icons/fa';
import { getTerminalName } from '../../utils/terminalNames';

const emptyTerminal = {
  guid: '',
  ubicacion: '',
  descripcion: '',
  latitud: '',
  longitud: '',
  tipo_cultivo: '',
  tipo_suelo: '',
  estado: true,
  id_cliente: '',
  sleep_sec: 300,
  tx_interval_sec: 300,
  sample_interval_sec: 30,
};

const sampleGuids = [
  'TRM-ACR-001',
  'TRM-ACR-002',
  'TRM-ACR-003',
  'TRM-ACR-004',
  'TRM-ACR-005',
  'TRM-ACR-006',
];

const AssignModal = ({ terminal, clients, onAssign, onClose }) => {
  const [clientId, setClientId] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!clientId) return;
    setLoading(true);
    await onAssign(terminal.guid, clientId);
    setLoading(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-bold text-gray-900 mb-4">Asignar Terminal</h2>
        <p className="text-sm text-gray-500 mb-4">Terminal: <span className="font-mono font-medium text-gray-800">{terminal.guid}</span></p>
        <select value={clientId} onChange={e => setClientId(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 outline-none mb-6">
          <option value="">Seleccionar cliente...</option>
          {clients.filter(c => c.estado === 'activo').map(c => (
            <option key={c.id} value={c.id}>{c.nombre} ({c.rut})</option>
          ))}
        </select>
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">Cancelar</button>
          <button onClick={handleSubmit} disabled={loading || !clientId}
            className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2 shadow-sm">
            {loading && <FaSpinner className="w-3.5 h-3.5 animate-spin" />}
            {loading ? 'Asignando...' : 'Asignar'}
          </button>
        </div>
      </div>
    </div>
  );
};

const TerminalsAdminPage = ({ token }) => {
  const [terminals, setTerminals] = useState([]);
  const [clients, setClients] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyTerminal);
  const [assignTarget, setAssignTarget] = useState(null);
  const [search, setSearch] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [remoteConfigMeta, setRemoteConfigMeta] = useState(null);
  const [guidMode, setGuidMode] = useState('sample');

  const terminalsWithName = useMemo(() => {
    return terminals.map(t => ({
      ...t,
      display_name: getTerminalName(t),
    }));
  }, [terminals]);

  const loadData = async () => {
    const [tRes, cRes] = await Promise.all([getAllTerminals(token), getClients(token)]);
    if (tRes.cod === 200) setTerminals(tRes.data);
    if (cRes.cod === 200) setClients(cRes.data);
  };

  useEffect(() => { loadData(); }, [token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editing) {
        await updateTerminal(token, editing, form);
        await updateTerminalRemoteConfig(token, editing, {
          sleep_sec: Number(form.sleep_sec),
          tx_interval_sec: Number(form.tx_interval_sec),
          sample_interval_sec: Number(form.sample_interval_sec),
        });
        const current = terminals.find(t => t.guid === editing);
        if (current && Number(form.id_cliente) !== Number(current.id_cliente)) {
          if (form.id_cliente) {
            await assignTerminal(token, editing, form.id_cliente);
          } else {
            await unassignTerminal(token, editing);
          }
        }
      } else {
        await createTerminal(token, form);
        await updateTerminalRemoteConfig(token, form.guid, {
          sleep_sec: Number(form.sleep_sec),
          tx_interval_sec: Number(form.tx_interval_sec),
          sample_interval_sec: Number(form.sample_interval_sec),
        });
        if (form.id_cliente) {
          await assignTerminal(token, form.guid, form.id_cliente);
        }
      }
      setShowForm(false);
      setEditing(null);
      setForm(emptyTerminal);
      setRemoteConfigMeta(null);
      loadData();
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (t) => {
    setLoadingConfig(true);
    setForm({
      guid: t.guid,
      ubicacion: t.ubicacion || '',
      descripcion: t.descripcion || '',
      latitud: t.latitud || '',
      longitud: t.longitud || '',
      tipo_cultivo: t.tipo_cultivo || '',
      tipo_suelo: t.tipo_suelo || '',
      estado: t.estado,
      id_cliente: t.id_cliente || '',
      sleep_sec: 300,
      tx_interval_sec: 300,
      sample_interval_sec: 30,
    });
    setEditing(t.guid);
    setShowForm(true);
    const cfg = await getTerminalRemoteConfig(token, t.guid);
    if (cfg.cod === 200 && cfg.data) {
      setForm(prev => ({
        ...prev,
        sleep_sec: cfg.data.sleep_sec ?? 300,
        tx_interval_sec: cfg.data.tx_interval_sec ?? 300,
        sample_interval_sec: cfg.data.sample_interval_sec ?? 30,
      }));
      setRemoteConfigMeta({
        version: cfg.data.version ?? 0,
        last_ack_version: cfg.data.last_ack_version ?? null,
        last_ack_at: cfg.data.last_ack_at ?? null,
      });
    } else {
      setRemoteConfigMeta(null);
    }
    setLoadingConfig(false);
  };

  const handleDelete = async (guid) => {
    if (!window.confirm('¿Eliminar esta terminal?')) return;
    await deleteTerminal(token, guid);
    loadData();
  };

  const handleAssign = async (guid, clientId) => {
    const res = await assignTerminal(token, guid, clientId);
    if (res.cod === 409) alert(res.message);
    loadData();
  };

  const handleUnassign = async (guid) => {
    if (!window.confirm('¿Desasignar esta terminal de su cliente?')) return;
    await unassignTerminal(token, guid);
    loadData();
  };

  const filtered = terminalsWithName.filter(t => {
    if (search && !t.guid?.toLowerCase().includes(search.toLowerCase()) &&
      !t.display_name?.toLowerCase().includes(search.toLowerCase()) &&
      !t.ubicacion?.toLowerCase().includes(search.toLowerCase()) &&
      !t.cliente_nombre?.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterClient && Number(t.id_cliente) !== Number(filterClient)) return false;
    if (filterEstado === 'activo' && !t.estado) return false;
    if (filterEstado === 'inactivo' && t.estado) return false;
    return true;
  });

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Terminales</h1>
          <p className="text-sm text-gray-500 mt-1">{terminals.length} terminal{terminals.length !== 1 ? 'es' : ''}</p>
        </div>
        <button onClick={() => { setForm(emptyTerminal); setEditing(null); setGuidMode('sample'); setShowForm(true); }}
          className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors shadow-sm">
          <FaPlus className="w-3.5 h-3.5" /> Nueva Terminal
        </button>
      </div>

      <div className="relative mb-4">
        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
          placeholder="Buscar por nombre, GUID, ubicación o cliente..." />
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select value={filterClient} onChange={e => setFilterClient(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 outline-none">
          <option value="">Todos los clientes</option>
          {clients.map(c => (
            <option key={c.id} value={c.id}>{c.nombre}</option>
          ))}
        </select>
        <select value={filterEstado} onChange={e => setFilterEstado(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 outline-none">
          <option value="">Todos los estados</option>
          <option value="activo">Activo</option>
          <option value="inactivo">Inactivo</option>
        </select>
        {(filterClient || filterEstado) && (
          <button onClick={() => { setFilterClient(''); setFilterEstado(''); }}
            className="text-xs text-gray-500 hover:text-gray-700 underline">
            Limpiar filtros
          </button>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => { setShowForm(false); setEditing(null); }}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-gray-900">{editing ? 'Editar Terminal' : 'Nueva Terminal'}</h2>
              <button onClick={() => { setShowForm(false); setEditing(null); }} className="p-1 hover:bg-gray-100 rounded-lg">
                <FaTimes className="w-4 h-4 text-gray-400" />
              </button>
            </div>
            <div className="space-y-4">
              {!editing && (
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">GUID *</label>
                  {guidMode === 'sample' ? (
                    <select
                      value={form.guid}
                      onChange={e => setForm({ ...form, guid: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                    >
                      <option value="">Seleccionar GUID sugerido...</option>
                      {sampleGuids.map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                  ) : (
                    <input name="guid" value={form.guid} onChange={e => setForm({ ...form, guid: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 outline-none" placeholder="ej: TRM-ACR-004" />
                  )}
                  <button
                    type="button"
                    onClick={() => setGuidMode(guidMode === 'sample' ? 'manual' : 'sample')}
                    className="mt-2 text-xs text-emerald-700 hover:text-emerald-800"
                  >
                    {guidMode === 'sample' ? 'Escribir GUID manual' : 'Usar GUID sugerido'}
                  </button>
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Ubicación</label>
                <input name="ubicacion" value={form.ubicacion} onChange={e => setForm({ ...form, ubicacion: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 outline-none" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Descripción</label>
                <input name="descripcion" value={form.descripcion} onChange={e => setForm({ ...form, descripcion: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 outline-none" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Cliente</label>
                <select value={form.id_cliente} onChange={e => setForm({ ...form, id_cliente: e.target.value ? Number(e.target.value) : '' })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 outline-none">
                  <option value="">Sin asignar</option>
                  {clients.filter(c => c.estado === 'activo').map(c => (
                    <option key={c.id} value={c.id}>{c.nombre} ({c.rut})</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Cultivo</label>
                  <select value={form.tipo_cultivo} onChange={e => setForm({ ...form, tipo_cultivo: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 outline-none">
                    <option value="">Sin configurar</option>
                    <option value="Uva">Uva</option>
                    <option value="Cerezo">Cerezo</option>
                    <option value="Arandano">Arándano</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Tipo de Suelo</label>
                  <select value={form.tipo_suelo} onChange={e => setForm({ ...form, tipo_suelo: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 outline-none">
                    <option value="">Sin configurar</option>
                    <option value="FrancoArenoso">Franco Arenoso</option>
                    <option value="Franco">Franco</option>
                    <option value="Arcilloso">Arcilloso</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Estado</label>
                <div className="flex items-center gap-3">
                  <button type="button" onClick={() => setForm({ ...form, estado: true })}
                    className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors ${form.estado ? 'bg-emerald-600 text-white shadow-sm' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                    Activo
                  </button>
                  <button type="button" onClick={() => setForm({ ...form, estado: false })}
                    className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors ${!form.estado ? 'bg-red-500 text-white shadow-sm' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                    Inactivo
                  </button>
                </div>
              </div>

              <div className="pt-2 border-t border-gray-100">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">Configuracion remota terminal (MQTT)</p>
                {loadingConfig ? (
                  <div className="text-xs text-gray-500">Cargando configuracion actual...</div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1 block">Sleep (seg)</label>
                      <input
                        type="number"
                        min="30"
                        max="86400"
                        value={form.sleep_sec}
                        onChange={e => setForm({ ...form, sleep_sec: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1 block">TX interval (seg)</label>
                      <input
                        type="number"
                        min="30"
                        max="86400"
                        value={form.tx_interval_sec}
                        onChange={e => setForm({ ...form, tx_interval_sec: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1 block">Sample (seg)</label>
                      <input
                        type="number"
                        min="5"
                        max="3600"
                        value={form.sample_interval_sec}
                        onChange={e => setForm({ ...form, sample_interval_sec: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                      />
                    </div>
                  </div>
                )}
                {remoteConfigMeta && (
                  <div className="mt-2 text-[11px] text-gray-500">
                    Version publicada: <span className="font-semibold text-gray-700">{remoteConfigMeta.version}</span>
                    {' · '}ACK: <span className="font-semibold text-gray-700">{remoteConfigMeta.last_ack_version ?? 'sin ACK'}</span>
                    {remoteConfigMeta.last_ack_at ? ` · ${new Date(remoteConfigMeta.last_ack_at).toLocaleString('es-CL')}` : ''}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Latitud</label>
                  <input name="latitud" value={form.latitud} onChange={e => setForm({ ...form, latitud: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 outline-none" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Longitud</label>
                  <input name="longitud" value={form.longitud} onChange={e => setForm({ ...form, longitud: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 outline-none" />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => { setShowForm(false); setEditing(null); }}
                className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">Cancelar</button>
              <button onClick={handleSave} disabled={saving || (!editing && !form.guid)}
                className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2 shadow-sm">
                {saving && <FaSpinner className="w-3.5 h-3.5 animate-spin" />}
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {assignTarget && (
        <AssignModal terminal={assignTarget} clients={clients} onAssign={handleAssign} onClose={() => setAssignTarget(null)} />
      )}

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Nombre</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">GUID</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Ubicación</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Perfil</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Cliente</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Estado</th>
                <th className="text-right px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(t => (
                <tr key={t.guid} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-gray-900">{t.display_name}</td>
                  <td className="px-4 py-3">
                    <span className="font-mono font-semibold text-gray-900 bg-gray-50 border border-gray-200 rounded px-2 py-1 inline-block">{t.guid}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-500">{t.ubicacion || '-'}</td>
                  <td className="px-4 py-3">
                    {t.tipo_cultivo || t.tipo_suelo ? (
                      <span className="text-xs text-gray-600">
                        {t.tipo_cultivo || '—'} / {t.tipo_suelo ? ({ FrancoArenoso: 'F. Arenoso', Franco: 'Franco', Arcilloso: 'Arcilloso' }[t.tipo_suelo] || t.tipo_suelo) : '—'}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400 italic">Sin perfil</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {t.cliente_nombre ? (
                      <span className="inline-flex items-center gap-2">
                        <span className="text-gray-800">{t.cliente_nombre}</span>
                        <button onClick={() => handleUnassign(t.guid)} className="p-1 text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Desasignar">
                          <FaUnlink className="w-3.5 h-3.5" />
                        </button>
                      </span>
                    ) : (
                      <span className="text-gray-400 italic text-xs">Sin asignar</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                      t.estado ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                    }`}>
                      {t.estado ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => handleEdit(t)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Editar">
                      <FaEdit className="w-4 h-4" />
                    </button>
                    {!t.id_cliente && (
                      <button onClick={() => setAssignTarget(t)} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors ml-1" title="Asignar">
                        <FaLink className="w-4 h-4" />
                      </button>
                    )}
                    <button onClick={() => handleDelete(t.guid)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors ml-1" title="Eliminar">
                      <FaTrash className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="p-8 text-center text-gray-400 text-sm">
            <FaSearch className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            {search ? 'No se encontraron terminales con ese criterio' : 'No hay terminales registradas'}
          </div>
        )}
      </div>
    </div>
  );
};

export default TerminalsAdminPage;
